import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const SUBJECTS_BY_CLASS = {
  6: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  7: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  8: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  9: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
  10: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
};

const Field = ({ label, value, editable, onChange, type = 'text', placeholder, hint, col }) => (
  <div style={{ gridColumn: col || 'auto' }}>
    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</label>
    {editable ? (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid #E2E8F0', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
        onFocus={e => e.target.style.borderColor = '#FF6B00'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
    ) : (
      <div style={{ padding: '8px 10px', background: '#F7F8FC', borderRadius: '8px', fontSize: '12px', color: '#64748B', border: '1.5px solid #E2E8F0' }}>{value || '—'}</div>
    )}
    {hint && <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '3px' }}>{hint}</div>}
  </div>
);

const AdminSchoolsPage = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(null);
  const [viewSchool, setViewSchool] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [schoolForm, setSchoolForm] = useState({ name: '', cbse_affiliation_no: '', principal_name: '', email: '', phone: '', city: '', state: '', password: '' });
  const [studentForm, setStudentForm] = useState({ name: '', email: '', phone: '', class_id: '10', subjects: SUBJECTS_BY_CLASS[10] || [], password: '' });
  const { success, error } = useToast();

  useEffect(() => { loadSchools(); }, []);

  const loadSchools = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/schools');
      setSchools(res.data.data || []);
    } catch { setSchools([]); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id, name) => {
    try { await api.post('/admin/schools/' + id + '/approve'); success(name + ' approved!'); loadSchools(); }
    catch { error('Failed.'); }
  };

  const handleViewSchool = (school) => {
    setViewSchool(school);
    setEditForm({ ...school });
    setEditMode(false);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put('/admin/schools/' + viewSchool.id, editForm);
      success('School details updated!');
      setViewSchool(editForm);
      setEditMode(false);
      loadSchools();
    } catch {
      error('Failed to update school');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSchool = async (e) => {
    e.preventDefault();
    try {
      await api.post('/schools/register', schoolForm);
      success('School registered! Approve it now.');
      setShowAddSchool(false);
      setSchoolForm({ name: '', cbse_affiliation_no: '', principal_name: '', email: '', phone: '', city: '', state: '', password: '' });
      loadSchools();
    } catch(err) { error(err.response?.data?.message || 'Failed to register school.'); }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/schools/students/add-admin', { ...studentForm, school_id: showAddStudent });
      success('Student added!');
      setShowAddStudent(null);
    } catch(err) { error(err.response?.data?.message || 'Failed.'); }
  };

  const statusStyle = (status) => {
    if (status === 'approved') return { bg: '#E6F4EC', color: '#1B7F3E', label: 'Approved' };
    if (status === 'pending') return { bg: '#FEF3C7', color: '#B45309', label: 'Pending' };
    if (status === 'rejected') return { bg: '#FEE2E2', color: '#DC2626', label: 'Rejected' };
    return { bg: '#F7F8FC', color: '#64748B', label: status };
  };

  return (
    <AdminShell title="School Management" subtitle={schools.length + ' schools registered'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowAddSchool(true)}
            style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
            + Register School
          </button>
        </div>

        {/* Schools table */}
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F7F8FC', borderBottom: '1px solid #E2E8F0' }}>
                {['School Name', 'CBSE No.', 'Email', 'City', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Loading...</td></tr>
              ) : schools.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>No schools registered yet</td></tr>
              ) : schools.map((s, i) => {
                const st = statusStyle(s.status);
                return (
                  <tr key={s.id} style={{ borderBottom: i < schools.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748B' }}>{s.principal_name}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#64748B' }}>{s.cbse_affiliation_no}</td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#64748B' }}>{s.email}</td>
                    <td style={{ padding: '12px 14px', fontSize: '12px' }}>{s.city}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: st.bg, color: st.color }}>{st.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => handleViewSchool(s)}
                          style={{ padding: '4px 10px', borderRadius: '6px', background: '#F7F8FC', color: '#1A3C6E', border: '1px solid #E2E8F0', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                          👁 View
                        </button>
                        {s.status === 'pending' && (
                          <button onClick={() => handleApprove(s.id, s.name)}
                            style={{ padding: '4px 10px', borderRadius: '6px', background: '#E6F4EC', color: '#1B7F3E', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                            ✓ Approve
                          </button>
                        )}
                        {s.status === 'approved' && (
                          <button onClick={() => { setShowAddStudent(s.id); setStudentForm(p => ({ ...p, subjects: SUBJECTS_BY_CLASS[10] || [] })); }}
                            style={{ padding: '4px 10px', borderRadius: '6px', background: '#E8F0FB', color: '#1A3C6E', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                            + Add Student
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* View/Edit School Modal */}
        {viewSchool && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            onClick={() => setViewSchool(null)}>
            <div style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 800 }}>🏫 {viewSchool.name}</div>
                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>CBSE Affiliation: {viewSchool.cbse_affiliation_no}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!editMode ? (
                    <button onClick={() => setEditMode(true)}
                      style={{ padding: '6px 14px', borderRadius: '8px', background: '#FF6B00', color: 'white', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                      ✏️ Edit
                    </button>
                  ) : (
                    <>
                      <button onClick={handleSaveEdit} disabled={saving}
                        style={{ padding: '6px 14px', borderRadius: '8px', background: '#1B7F3E', color: 'white', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                        {saving ? 'Saving...' : '💾 Save'}
                      </button>
                      <button onClick={() => { setEditMode(false); setEditForm({ ...viewSchool }); }}
                        style={{ padding: '6px 14px', borderRadius: '8px', background: '#F7F8FC', color: '#64748B', border: '1px solid #E2E8F0', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </>
                  )}
                  <button onClick={() => setViewSchool(null)}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#F7F8FC', border: '1px solid #E2E8F0', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ×
                  </button>
                </div>
              </div>

              {/* Modal body */}
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Status badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', ...(() => { const st = statusStyle(viewSchool.status); return { background: st.bg, color: st.color }; })() }}>
                    {statusStyle(viewSchool.status).label}
                  </span>
                  <span style={{ fontSize: '11px', color: '#94A3B8' }}>Registered: {viewSchool.created_at ? new Date(viewSchool.created_at).toLocaleDateString('en-IN') : 'N/A'}</span>
                  {viewSchool.partnership_signed_at && <span style={{ fontSize: '11px', color: '#94A3B8' }}>· Partnership signed: {new Date(viewSchool.partnership_signed_at).toLocaleDateString('en-IN')}</span>}
                </div>

                {/* School details */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>School Information</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <Field label="School Name" value={editForm.name} editable={editMode} onChange={v => setEditForm(p => ({ ...p, name: v }))} col="1/-1" />
                    <Field label="CBSE Affiliation No." value={editForm.cbse_affiliation_no} editable={editMode} onChange={v => setEditForm(p => ({ ...p, cbse_affiliation_no: v }))} />
                    <Field label="Principal Name" value={editForm.principal_name} editable={editMode} onChange={v => setEditForm(p => ({ ...p, principal_name: v }))} />
                    <Field label="Email" value={editForm.email} editable={editMode} onChange={v => setEditForm(p => ({ ...p, email: v }))} />
                    <Field label="Phone" value={editForm.phone} editable={editMode} onChange={v => setEditForm(p => ({ ...p, phone: v }))} />
                    <Field label="Address" value={editForm.address} editable={editMode} onChange={v => setEditForm(p => ({ ...p, address: v }))} col="1/-1" />
                    <Field label="City" value={editForm.city} editable={editMode} onChange={v => setEditForm(p => ({ ...p, city: v }))} />
                    <Field label="State" value={editForm.state} editable={editMode} onChange={v => setEditForm(p => ({ ...p, state: v }))} />
                    <Field label="Pincode" value={editForm.pincode} editable={editMode} onChange={v => setEditForm(p => ({ ...p, pincode: v }))} />
                  </div>
                </div>

                {/* Contact person */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>Contact Person</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <Field label="Contact Person Name" value={editForm.contact_person_name} editable={editMode} onChange={v => setEditForm(p => ({ ...p, contact_person_name: v }))} placeholder="e.g. IT Coordinator" />
                    <Field label="Designation" value={editForm.contact_person_designation} editable={editMode} onChange={v => setEditForm(p => ({ ...p, contact_person_designation: v }))} placeholder="e.g. Teacher, Coordinator" />
                    <Field label="Approx. Total Students" value={editForm.total_students_approx} editable={editMode} onChange={v => setEditForm(p => ({ ...p, total_students_approx: v }))} placeholder="e.g. 500" type="number" />
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Add School Modal */}
        {showAddSchool && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: 'white', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '16px' }}>🏫 Register School</div>
              <form onSubmit={handleAddSchool}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  {[
                    { key: 'name', label: 'School Name *', placeholder: 'Delhi Public School', col: '1/-1' },
                    { key: 'cbse_affiliation_no', label: 'CBSE Affiliation No. *', placeholder: '1100123' },
                    { key: 'principal_name', label: 'Principal Name', placeholder: 'Full name' },
                    { key: 'email', label: 'Email *', placeholder: 'school@email.com', type: 'email' },
                    { key: 'phone', label: 'Phone', placeholder: '10-digit number' },
                    { key: 'city', label: 'City', placeholder: 'City' },
                    { key: 'state', label: 'State', placeholder: 'State' },
                    { key: 'contact_person_name', label: 'Contact Person', placeholder: 'Name' },
                    { key: 'contact_person_designation', label: 'Designation', placeholder: 'e.g. Coordinator' },
                    { key: 'total_students_approx', label: 'Approx Students', placeholder: 'e.g. 500', type: 'number' },
                    { key: 'password', label: 'Password *', placeholder: 'Min. 8 characters', type: 'password', col: '1/-1' },
                  ].map(f => (
                    <div key={f.key} style={{ gridColumn: f.col || 'auto' }}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                      <input type={f.type || 'text'} value={schoolForm[f.key] || ''} onChange={e => setSchoolForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" style={{ flex: 1, background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Register School</button>
                  <button type="button" onClick={() => setShowAddSchool(false)} style={{ padding: '10px 16px', background: '#F7F8FC', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Student Modal */}
        {showAddStudent && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: 'white', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '16px' }}>👨‍🎓 Add Student</div>
              <form onSubmit={handleAddStudent}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  {[
                    { key: 'name', label: 'Student Name *', placeholder: 'Full name', col: '1/-1' },
                    { key: 'email', label: 'Email', placeholder: 'optional', type: 'email' },
                    { key: 'phone', label: 'Phone', placeholder: '10-digit' },
                    { key: 'password', label: 'Password *', placeholder: 'Min. 8 chars', type: 'password', col: '1/-1' },
                  ].map(f => (
                    <div key={f.key} style={{ gridColumn: f.col || 'auto' }}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                      <input type={f.type || 'text'} value={studentForm[f.key] || ''} onChange={e => setStudentForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '4px' }}>Class *</label>
                  <select value={studentForm.class_id} onChange={e => { const cls = parseInt(e.target.value); setStudentForm(p => ({ ...p, class_id: e.target.value, subjects: SUBJECTS_BY_CLASS[cls] || [] })); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px', outline: 'none' }}>
                    {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '6px' }}>Subjects</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {(SUBJECTS_BY_CLASS[parseInt(studentForm.class_id)] || []).map(s => (
                      <button key={s} type="button"
                        onClick={() => setStudentForm(p => ({ ...p, subjects: p.subjects.includes(s) ? p.subjects.filter(x => x !== s) : [...p.subjects, s] }))}
                        style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, border: studentForm.subjects.includes(s) ? 'none' : '1px solid #E2E8F0', background: studentForm.subjects.includes(s) ? '#1A3C6E' : 'white', color: studentForm.subjects.includes(s) ? 'white' : '#64748B', cursor: 'pointer' }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" style={{ flex: 1, background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Add Student</button>
                  <button type="button" onClick={() => setShowAddStudent(null)} style={{ padding: '10px 16px', background: '#F7F8FC', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminSchoolsPage;
